import { useState } from 'react';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader2, Mail, KeyRound, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

/** Supabase's `shouldCreateUser: false` rejection reads like a server bug; say what it means. */
function friendlyAuthError(message: string | undefined): string {
  if (!message) return 'Something went wrong. Please try again.';
  if (/signups? not allowed/i.test(message)) return 'No admin account exists for that email.';
  if (/token has expired|otp_expired/i.test(message)) return 'That code has expired. Send a new one.';
  if (/invalid|not found/i.test(message)) return 'That code is not right. Check the email and try again.';
  return message;
}

export default function AdminLogin() {
  const { signInWithPassword, signInWithMagicLink, verifyEmailCode, loading, error } = useAdminAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [magicEmail, setMagicEmail] = useState('');
  const [magicSent, setMagicSent] = useState(false);
  const [magicCode, setMagicCode] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await signInWithPassword(email, password);
      toast.success('Signed in successfully');
    } catch (err: any) {
      toast.error(err.message || 'Sign in failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await signInWithMagicLink(magicEmail);
      setMagicSent(true);
      setMagicCode('');
      toast.success('Code sent. Check your email.');
    } catch (err: any) {
      toast.error(friendlyAuthError(err?.message));
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await verifyEmailCode(magicEmail, magicCode);
      toast.success('Signed in successfully');
    } catch (err: any) {
      toast.error(friendlyAuthError(err?.message));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="relative min-h-screen flex items-center justify-center p-4 overflow-hidden"
      style={{ background: '#070b13' }}
    >
      {/* Ambient brand glows */}
      <div
        className="pointer-events-none absolute top-[-15%] left-[-10%] w-[42rem] h-[42rem] rounded-full opacity-30 blur-[140px]"
        style={{
          background:
            'radial-gradient(closest-side, #06b6d4 0%, #0891b2 40%, transparent 75%)',
        }}
      />
      <div
        className="pointer-events-none absolute bottom-[-15%] right-[-10%] w-[44rem] h-[44rem] rounded-full opacity-25 blur-[160px]"
        style={{
          background:
            'radial-gradient(closest-side, #10b981 0%, #064e3b 40%, transparent 80%)',
        }}
      />
      <div
        className="pointer-events-none absolute top-1/3 right-1/4 w-[18rem] h-[18rem] rounded-full opacity-20 blur-[120px]"
        style={{
          background:
            'radial-gradient(closest-side, #fbbf24 0%, transparent 70%)',
        }}
      />

      {/* Faint topographic contour overlay */}
      <svg
        className="absolute inset-0 w-full h-full contour-animate opacity-[0.05] pointer-events-none"
        viewBox="0 0 800 600"
        preserveAspectRatio="xMidYMid slice"
      >
        <path d="M100,500 Q200,400 300,450 T500,350 T700,400" fill="none" stroke="white" strokeWidth="1.5" />
        <path d="M50,450 Q200,350 350,380 T550,300 T750,350" fill="none" stroke="white" strokeWidth="1" />
        <path d="M0,400 Q180,300 380,320 T600,250 T800,300" fill="none" stroke="white" strokeWidth="0.8" />
        <path d="M80,350 Q250,250 400,270 T620,200 T800,240" fill="none" stroke="white" strokeWidth="0.6" />
      </svg>

      <div className="relative w-full max-w-md">
        {/* Logo / Brand */}
        <div className="text-center mb-8">
          <img
            src="/logo-transparent.png"
            alt="H-Plate"
            className="w-20 h-20 mx-auto mb-5 object-contain animate-float logo-glow"
          />
          <h1 className="font-display text-3xl text-white tracking-tight">
            H-Plate
          </h1>
          <p className="text-[11px] uppercase tracking-[0.25em] font-semibold text-cyan-300/80 mt-1">
            Control Room
          </p>
        </div>

        <Card
          className="border-white/10 shadow-2xl"
          style={{
            background: 'rgba(17,24,39,0.7)',
            backdropFilter: 'blur(18px) saturate(140%)',
            WebkitBackdropFilter: 'blur(18px) saturate(140%)',
          }}
        >
          <CardHeader className="pb-4">
            <CardTitle className="text-white text-lg flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-cyan-300" />
              Sign in
            </CardTitle>
            <CardDescription className="text-gray-400">
              Access the admin portal with your authorized account.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {error && (
              <div className="mb-4 p-3 rounded-lg bg-rose-500/10 border border-rose-500/25 text-rose-300 text-sm">
                {friendlyAuthError(error)}
              </div>
            )}

            <Tabs defaultValue="password" className="w-full">
              <TabsList
                className="grid w-full grid-cols-2 border border-white/10"
                style={{ background: 'rgba(17,24,39,0.6)' }}
              >
                <TabsTrigger
                  value="password"
                  className="text-gray-300 data-[state=active]:text-white data-[state=active]:bg-gradient-to-r data-[state=active]:from-cyan-500/20 data-[state=active]:to-emerald-500/15 data-[state=active]:shadow-[inset_0_0_0_1px_rgba(6,182,212,0.3)]"
                >
                  <KeyRound className="w-3.5 h-3.5 mr-1.5" />
                  Password
                </TabsTrigger>
                <TabsTrigger
                  value="magic"
                  className="text-gray-300 data-[state=active]:text-white data-[state=active]:bg-gradient-to-r data-[state=active]:from-cyan-500/20 data-[state=active]:to-emerald-500/15 data-[state=active]:shadow-[inset_0_0_0_1px_rgba(6,182,212,0.3)]"
                >
                  <Mail className="w-3.5 h-3.5 mr-1.5" />
                  Magic Link
                </TabsTrigger>
              </TabsList>

              <TabsContent value="password" className="mt-4">
                <form onSubmit={handlePasswordLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-gray-300">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="admin@h-plate.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="bg-white/[0.04] border-white/10 text-white placeholder:text-gray-500 focus:border-cyan-400/50 focus:ring-cyan-400/20"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-gray-300">Password</Label>
                    <Input
                      id="password"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="bg-white/[0.04] border-white/10 text-white placeholder:text-gray-500 focus:border-cyan-400/50 focus:ring-cyan-400/20"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full text-white font-semibold border-0"
                    style={{
                      background:
                        'linear-gradient(135deg, #06b6d4 0%, #10b981 100%)',
                      boxShadow:
                        '0 8px 24px -8px rgba(6,182,212,0.5)',
                    }}
                    disabled={submitting || loading}
                  >
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Sign in
                  </Button>
                </form>
              </TabsContent>

              <TabsContent value="magic" className="mt-4">
                {magicSent ? (
                  <form onSubmit={handleVerifyCode} className="space-y-4">
                    <div className="text-center pt-2 pb-1">
                      <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-cyan-500/10 border border-cyan-500/25 mb-2">
                        <Mail className="w-6 h-6 text-cyan-300" />
                      </div>
                      <p className="text-white font-medium">Enter the code from your email</p>
                      <p className="text-gray-400 text-sm mt-1">
                        We sent a sign-in code to{' '}
                        <span className="text-cyan-300">{magicEmail}</span>. It expires in one hour.
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="magic-code" className="text-gray-300">Sign-in code</Label>
                      <Input
                        id="magic-code"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        placeholder="12345678"
                        value={magicCode}
                        onChange={(e) => setMagicCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
                        required
                        autoFocus
                        className="bg-white/[0.04] border-white/10 text-white text-center text-lg tracking-[0.35em] font-mono placeholder:text-gray-600 placeholder:tracking-[0.35em] focus:border-cyan-400/50 focus:ring-cyan-400/20"
                      />
                    </div>
                    <Button
                      type="submit"
                      className="w-full text-white font-semibold border-0"
                      style={{
                        background:
                          'linear-gradient(135deg, #06b6d4 0%, #10b981 100%)',
                        boxShadow:
                          '0 8px 24px -8px rgba(6,182,212,0.5)',
                      }}
                      disabled={submitting || loading || magicCode.length < 6}
                    >
                      {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      Verify and sign in
                    </Button>
                    <div className="flex items-center justify-between text-sm">
                      <button
                        type="button"
                        className="text-gray-400 hover:text-cyan-300 disabled:opacity-50"
                        disabled={submitting}
                        onClick={(e) => { void handleMagicLink(e); }}
                      >
                        Resend code
                      </button>
                      <button
                        type="button"
                        className="text-gray-400 hover:text-cyan-300"
                        onClick={() => { setMagicSent(false); setMagicCode(''); }}
                      >
                        Use a different email
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 text-center">
                      Once you are in, set a new password under Settings.
                    </p>
                  </form>
                ) : (
                  <form onSubmit={handleMagicLink} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="magic-email" className="text-gray-300">Email</Label>
                      <Input
                        id="magic-email"
                        type="email"
                        placeholder="admin@h-plate.com"
                        value={magicEmail}
                        onChange={(e) => setMagicEmail(e.target.value)}
                        required
                        className="bg-white/[0.04] border-white/10 text-white placeholder:text-gray-500 focus:border-cyan-400/50 focus:ring-cyan-400/20"
                      />
                    </div>
                    <Button
                      type="submit"
                      className="w-full text-white font-semibold border-0"
                      style={{
                        background:
                          'linear-gradient(135deg, #06b6d4 0%, #10b981 100%)',
                        boxShadow:
                          '0 8px 24px -8px rgba(6,182,212,0.5)',
                      }}
                      disabled={submitting || loading}
                    >
                      {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      Send magic link
                    </Button>
                  </form>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-gray-500 mt-6">
          Only authorized platform administrators can access this portal.
        </p>
      </div>
    </div>
  );
}
